import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorMode,
  useToast,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { IoMdAdd } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { DeviceBox } from "./DeviceBox";
import API from "../../context/API";

const Devices = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentDevice, setCurrentDevice] = useState(null);
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const toast = useToast();
  const { colorMode } = useColorMode();

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  async function fetchDevices() {
    try {
      const devicesResponse = await API.get("devices", {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      const devicesData = devicesResponse.data.data;
      setDevices(devicesData);
      setFilteredDevices(devicesData);
    } catch (error) {
      toast({
        title: "Error fetching devices.",
        description: error.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }

  async function fetchDeviceTypes() {
    try {
      const typesResponse = await API.get("device-types", {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      setDeviceTypes(typesResponse.data.data);
    } catch (error) {
      toast({
        title: "Error fetching device types.",
        description: error.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }

  useEffect(() => {
    fetchDevices();
    fetchDeviceTypes();
    return () => {
      setDevices([]);
      setDeviceTypes([]);
    };
  }, []);

  const handleTypeFilter = (event) => {
    const type = event.target.value;
    setSelectedType(type);
    if (!type) {
      setFilteredDevices(devices);
    } else {
      const filtered = devices.filter((device) => device.type?.id === type);
      setFilteredDevices(filtered);
    }
  };

  const handleSearch = (event) => {
    const filter = event.target.value.toLowerCase();
    if (!filter) {
      setFilteredDevices(devices);
      return;
    }
    const filtered = devices.filter(
      (item) =>
        item.name.toLowerCase().includes(filter) ||
        item.identifier.toLowerCase().includes(filter) ||
        (item.type && item.type.name.toLowerCase().includes(filter))
    );
    setFilteredDevices(filtered);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    filteredDevices.sort((a, b) => {
      const aValue =
        key === "type" || key === "merchant" ? a[key]?.name : a[key];
      const bValue =
        key === "type" || key === "merchant" ? b[key]?.name : b[key];

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const getArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? <TriangleUpIcon /> : <TriangleDownIcon />;
    }
    return "↕";
  };

  return (
    <>
      {!isEditing ? (
        <>
          <Box>
            <Flex
              justify={"space-between"}
              alignItems={"center"}
              width={"100%"}
              flexWrap={"wrap"}
              mt={4}
            >
              <Heading size={"xl"} letterSpacing={-2}>
                Devices
              </Heading>
              <Box>
                <Button
                  leftIcon={<IoMdAdd />}
                  bg="black"
                  color={"white"}
                  _hover={{
                    color: "black",
                    bg: "white",
                    borderWidth: "1px",
                    borderColor: "gray.300",
                  }}
                  variant="outline"
                  onClick={() => {
                    setCurrentDevice(null);
                    setIsEditing(true);
                  }}
                >
                  Add Device
                </Button>
              </Box>
            </Flex>
            <HStack display={"flex"} justifyContent={"start"} alignItems={"center"} m={4}>
              <InputGroup size={"md"}>
                <InputLeftElement
                  pointerEvents="none"
                  children={<SearchIcon color="gray.300" />}
                />
                <Input
                  type="search"
                  placeholder="Search..."
                  _placeholder={{ color: "gray.500" }}
                  onKeyUp={handleSearch}
                />
              </InputGroup>
              <Select
                placeholder="Filter by Device Type"
                onChange={handleTypeFilter}
                value={selectedType}
              >
                {deviceTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Select>
            </HStack>
          </Box>
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th onClick={() => handleSort("name")}>
                    Name {getArrow("name")}
                  </Th>
                  <Th onClick={() => handleSort("identifier")}>
                    Identifier {getArrow("identifier")}
                  </Th>
                  <Th onClick={() => handleSort("type")}>
                    Type {getArrow("type")}
                  </Th>
                  <Th onClick={() => handleSort("merchant")}>
                    Merchant {getArrow("merchant")}
                  </Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredDevices.map((device) => (
                  <Tr key={device.id}>
                    <Td>{device.name}</Td>
                    <Td>{device.identifier}</Td>
                    <Td>{device.type?.name}</Td>
                    <Td>{device.merchant?.name}</Td>
                    <Td>
                      <Button
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => {
                          // Add delete logic here
                        }}
                      >
                        <MdDelete />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <DeviceBox
          initialDevice={currentDevice}
          onSave={() => setIsEditing(false)}
          onCancel={() => setIsEditing(false)}
          fetchDevices={fetchDevices}
        />
      )}
    </>
  );
};

export default Devices;
